import type { Provenance } from '@quizee/core';
import type { Interaction } from '../api/types';

/**
 * Reading the provenance flag: who wrote this, and whether a person has stood behind it.
 *
 * The server refuses to publish an AI drafted interaction that carries no approval, and this is
 * the editor's copy of that same rule. It is deliberately conservative in one direction: anything
 * it cannot read as an approved AI draft is treated as needing approval, and anything with no
 * provenance at all is treated as human, because inventing an approval is the one mistake here
 * that a publisher could not undo.
 */

export function isAiDraft(interaction: Interaction | null): boolean {
  return interaction?.provenance?.source === 'ai_draft';
}

export interface Approval {
  /** The user id of whoever approved it. Names are looked up separately. */
  readonly by: string;
  readonly at: string | null;
}

/** The approval on an interaction, or null if nobody has approved it. */
export function approvalOf(interaction: Interaction | null): Approval | null {
  const provenance = interaction?.provenance;
  if (provenance === undefined || provenance.source !== 'ai_draft') return null;
  const by = provenance.approved_by;
  if (by === undefined || by === '') return null;
  return { by, at: provenance.approved_at ?? null };
}

/** True while a model drafted this and nobody has approved it, which is what blocks publishing. */
export function awaitingApproval(interaction: Interaction | null): boolean {
  return isAiDraft(interaction) && approvalOf(interaction) === null;
}

export function modelOf(interaction: Interaction | null): string | null {
  const provenance: Provenance | undefined = interaction?.provenance;
  if (provenance === undefined || provenance.source !== 'ai_draft') return null;
  const model = provenance.model;
  return model === undefined || model.trim() === '' ? null : model;
}

/**
 * The name of a model, for people who have never seen a model id.
 *
 * Bedrock ids look like `anthropic.claude-sonnet-5` or, for a dated snapshot,
 * `anthropic.claude-haiku-4-5-20251001-v1:0`. Both say the same thing to an editor once the vendor
 * prefix and the snapshot suffix are off, so the id is unpicked rather than looked up in a table
 * that would go stale the first time the model changes. Anything that does not fit the shape is
 * shown exactly as it arrived, which is still a true answer to "which model wrote this".
 */
export function modelLabel(model: string): string {
  const id = model.trim();
  if (id === '') return 'a model';

  const withoutVendor = id.replace(/^[a-z0-9]+\./, '');
  const withoutSnapshot = withoutVendor.replace(/-v\d+(?::\d+)?$/, '').replace(/-\d{8}$/, '');
  const parts = withoutSnapshot.split('-');
  const [maker, family, ...version] = parts;

  if (maker !== 'claude' || family === undefined || family === '') return id;
  if (version.some((part) => !/^\d+$/.test(part))) return id;

  const name = `Claude ${family.charAt(0).toUpperCase()}${family.slice(1)}`;
  return version.length === 0 ? name : `${name} ${version.join('.')}`;
}
